'use client';
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Icon from '@/components/ui/AppIcon';
import SentimentBadge, { SentimentType } from '@/components/ui/SentimentBadge';
import type { CallState } from './ActiveInteractionContent';
import { toast } from 'sonner';

interface Props {
  callState: CallState;
  onOpenKnowledge?: () => void;
}

type AIPhase = 'listening' | 'intent' | 'sentiment' | 'knowledge' | 'action' | 'complete';

const phaseDelays: Record<AIPhase, number> = {
  listening: 0,
  intent: 3000,
  sentiment: 6000,
  knowledge: 9500,
  action: 13000,
  complete: 999999,
};

type SentimentEvent = {
  time: string;
  sentiment: SentimentType;
  confidence: number;
  context: string;
};

const sentimentTimeline: SentimentEvent[] = [
  { time: '00:00', sentiment: 'Neutral', confidence: 72, context: 'Call connected — initial greeting' },
  { time: '01:42', sentiment: 'Concerned', confidence: 84, context: 'Citizen mentions difficulty with MySejahtera' },
  { time: '03:20', sentiment: 'Frustrated', confidence: 89, context: 'Repeated failed attempts mentioned' },
  { time: '05:54', sentiment: 'Satisfied', confidence: 91, context: 'Agent provided clear guidance — citizen understood' },
];

function getSentimentEmoji(sentiment: SentimentType): string {
  switch (sentiment) {
    case 'Positive': return '😊';
    case 'Satisfied': return '🙂';
    case 'Neutral': return '😐';
    case 'Concerned': return '😕';
    case 'Frustrated': return '😣';
    case 'Angry': return '😠';
    default: return '😐';
  }
}

function SentimentTooltip({ event, anchorEl }: { event: SentimentEvent; anchorEl: HTMLElement | null }) {
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!anchorEl) return;
    const rect = anchorEl.getBoundingClientRect();
    const tooltipWidth = 160;
    const tooltipHeight = 72;
    const spaceAbove = rect.top;
    const spaceBelow = window.innerHeight - rect.bottom;
    let top: number;
    if (spaceAbove >= tooltipHeight + 8) {
      top = rect.top - tooltipHeight - 8;
    } else if (spaceBelow >= tooltipHeight + 8) {
      top = rect.bottom + 8;
    } else {
      top = Math.max(8, rect.top - tooltipHeight / 2);
    }
    let left = rect.left + rect.width / 2 - tooltipWidth / 2;
    left = Math.max(8, Math.min(left, window.innerWidth - tooltipWidth - 8));
    setPos({ top, left });
  }, [anchorEl]);

  return createPortal(
    <div
      className="fixed bg-foreground text-white text-[10px] rounded-xl px-3 py-2 shadow-elevated pointer-events-none"
      style={{ top: pos.top, left: pos.left, zIndex: 9999, width: 160 }}
    >
      <div className="font-700">{event.time} · {event.sentiment}</div>
      <div className="opacity-80">{event.confidence}% confidence</div>
      <div className="opacity-70 mt-0.5 leading-snug">{event.context}</div>
    </div>,
    document.body
  );
}

export default function AIAssistColumn({ callState, onOpenKnowledge }: Props) {
  const [phase, setPhase] = useState<AIPhase>('listening');
  const [currentSentiment, setCurrentSentiment] = useState<SentimentType>('Neutral');
  const [sentimentConfidence, setSentimentConfidence] = useState(72);
  const [hoveredEvent, setHoveredEvent] = useState<number | null>(null);
  const [hoveredEl, setHoveredEl] = useState<HTMLElement | null>(null);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (callState !== 'active') return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    const phases: AIPhase[] = ['intent', 'sentiment', 'knowledge', 'action'];
    phases.forEach((p) => {
      const t = setTimeout(() => setPhase(p), phaseDelays[p]);
      timers.push(t);
    });

    // Sentiment progression: Concerned → Frustrated → Satisfied
    const s1 = setTimeout(() => { setCurrentSentiment('Concerned'); setSentimentConfidence(84); }, 6000);
    const s2 = setTimeout(() => { setCurrentSentiment('Frustrated'); setSentimentConfidence(89); }, 12000);
    const s3 = setTimeout(() => { setCurrentSentiment('Satisfied'); setSentimentConfidence(91); }, 22000);
    timers.push(s1, s2, s3);

    return () => timers.forEach(clearTimeout);
  }, [callState]);

  const phaseReached = (p: AIPhase) => {
    const order: AIPhase[] = ['listening', 'intent', 'sentiment', 'knowledge', 'action', 'complete'];
    return order.indexOf(phase) >= order.indexOf(p);
  };

  const isProcessing = callState === 'ai-processing';
  const isDraftReady = callState === 'ai-draft-ready';

  return (
    <div className="p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon name="SparklesIcon" size={14} className="text-primary" />
          <h2 className="text-[13px] font-700 text-primary">CX AI Assist</h2>
          <span className="ai-badge">AI</span>
        </div>
        <div className="text-[10px] text-muted-foreground">Knowledge-grounded</div>
      </div>

      {/* Status */}
      <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-[12px] font-600 ${
        callState === 'active' ? 'bg-blue-50 border-blue-200 text-blue-700' : isProcessing ?'bg-blue-50 border-blue-200 text-blue-700': isDraftReady ?'bg-purple-50 border-purple-200 text-purple-700': 'bg-green-50 border-green-200 text-green-700'
      }`}>
        {callState === 'active' ? (
          <>
            <div className="flex gap-0.5">
              {[0, 1, 2].map((i) => (
                <span key={`dot-${i}`} className="w-1.5 h-1.5 bg-blue-500 rounded-full ai-listening-dot" style={{ animationDelay: `${i * 0.16}s` }} />
              ))}
            </div>
            {phase === 'listening' ? 'Listening & Analysing' :
             phase === 'intent' ? 'Intent Detected' :
             phase === 'sentiment' ? 'Sentiment Detected' :
             phase === 'knowledge'? 'Knowledge Matched' : 'Analysis Complete'}
          </>
        ) : isProcessing ? (
          <>
            <div className="flex gap-0.5">
              {[0, 1, 2].map((i) => (
                <span key={`dot-${i}`} className="w-1.5 h-1.5 bg-blue-500 rounded-full ai-listening-dot" style={{ animationDelay: `${i * 0.16}s` }} />
              ))}
            </div>
            Generating Interaction Summary...
          </>
        ) : isDraftReady ? (
          <>
            <span>✨</span>
            AI Draft Ready — Review Required
          </>
        ) : (
          <>
            <Icon name="CheckCircleIcon" size={14} />
            Interaction Analysis Complete
          </>
        )}
      </div>

      {/* Stage 1: Listening & Recording indicator */}
      {callState === 'active' && phase === 'listening' && (
        <div className="card-elevated p-3 fade-in-up">
          <div className="flex items-center gap-1.5 mb-2">
            <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse flex-shrink-0" />
            <span className="text-[11px] font-700 text-muted-foreground">Mitel MIR Recording Active</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-muted/50 border border-border text-[11px] text-muted-foreground">
            <Icon name="SparklesIcon" size={12} className="text-primary" />
            <span>CX AI Assist is listening and analysing...</span>
          </div>
        </div>
      )}

      {/* Recording indicator (always shown when active) */}
      {callState === 'active' && phase !== 'listening' && (
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-muted/50 border border-border text-[11px] text-muted-foreground">
          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse flex-shrink-0" />
          <span>Recording Active · Mitel MIR</span>
          <span className="ml-auto text-[10px]">CX AI Listening</span>
        </div>
      )}

      {/* Stage 2: Intent Detection */}
      {phaseReached('intent') && (
        <div className="card-elevated p-3 fade-in-up">
          <div className="flex items-center gap-1.5 mb-2">
            <Icon name="LightBulbIcon" size={13} className="text-amber-500" />
            <span className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground">Detected Intent</span>
          </div>
          <div className="text-[13px] font-700 text-foreground mb-1.5">MySejahtera Mobile Number Update</div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
              <div className="h-full bg-primary rounded-full" style={{ width: '94%' }} />
            </div>
            <span className="text-[11px] font-700 text-primary">94%</span>
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            {['MySejahtera', 'Account Update', 'Mobile Number', 'KKM'].map((t) => (
              <span key={`kw-${t}`} className="text-[10px] bg-secondary text-primary px-1.5 py-0.5 rounded-md font-500 border border-blue-200">{t}</span>
            ))}
          </div>
        </div>
      )}

      {/* Stage 3: Sentiment */}
      {phaseReached('sentiment') && callState === 'active' && (
        <div className="card-elevated p-3 fade-in-up">
          <div className="flex items-center gap-1.5 mb-2">
            <Icon name="FaceSmileIcon" size={13} className="text-amber-500" />
            <span className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground">Live Sentiment</span>
          </div>
          <div className="flex items-center justify-between mb-2">
            <SentimentBadge sentiment={currentSentiment} showBg confidence={sentimentConfidence} size="md" />
          </div>
          {/* Sentiment timeline */}
          <div className="text-[10px] text-muted-foreground mb-1.5 font-600">Sentiment Journey</div>
          <div className="flex items-center gap-1 mb-1">
            {sentimentTimeline.slice(0, 3).map((e, i) => (
              <React.Fragment key={i}>
                <div
                  className="relative cursor-pointer"
                  onMouseEnter={(ev) => { setHoveredEvent(i); setHoveredEl(ev.currentTarget as HTMLElement); }}
                  onMouseLeave={() => { setHoveredEvent(null); setHoveredEl(null); }}
                  onClick={() => toast.info(`${e.time}: ${e.sentiment} (${e.confidence}%) — ${e.context}`)}
                >
                  <span className="text-[18px] select-none">{getSentimentEmoji(e.sentiment)}</span>
                  {mounted && hoveredEvent === i && hoveredEl && (
                    <SentimentTooltip event={e} anchorEl={hoveredEl} />
                  )}
                </div>
                {i < 2 && <Icon name="ArrowRightIcon" size={10} className="text-muted-foreground" />}
              </React.Fragment>
            ))}
          </div>
          <div className="text-[10px] text-muted-foreground">Tap/hover for details</div>
        </div>
      )}

      {/* Stage 4: Knowledge Suggestions */}
      {phaseReached('knowledge') && (
        <div className="card-elevated p-3 fade-in-up">
          <div className="flex items-center gap-1.5 mb-2">
            <Icon name="BookOpenIcon" size={13} className="text-primary" />
            <span className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground">Suggested Knowledge</span>
            <span className="verified-badge ml-auto">Approved</span>
          </div>
          <div className="space-y-2">
            {[
              {
                title: 'Updating Mobile Number in MySejahtera',
                agency: 'Kementerian Kesihatan Malaysia',
                updated: 'Recently',
                relevance: 97,
                primary: true,
              },
              {
                title: 'MySejahtera Account Recovery Procedure',
                agency: 'Kementerian Kesihatan Malaysia',
                updated: 'Recently',
                relevance: 82,
                primary: false,
              },
            ].map((art, i) => (
              <div key={`art-${i}`} className={`rounded-xl border p-2.5 ${art.primary ? 'border-primary/30 bg-secondary/50' : 'border-border bg-white'}`}>
                <div className="text-[12px] font-600 text-foreground mb-1 leading-snug">{art.title}</div>
                <div className="flex items-center gap-1 mb-1">
                  <span className="text-[9px] bg-green-50 text-green-700 border border-green-200 px-1.5 py-0.5 rounded font-600">Approved Government Knowledge</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-[10px] text-muted-foreground">{art.agency} · {art.relevance}% relevance</div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => onOpenKnowledge ? onOpenKnowledge() : toast.info(`Opening: ${art.title}`)}
                      className="text-[11px] text-primary font-600 hover:underline"
                    >
                      Open Article
                    </button>
                    {art.primary && (
                      <button
                        onClick={() => toast.success('Guidance inserted into response')}
                        className="text-[11px] text-green-700 font-600 hover:underline ml-2"
                      >
                        Insert
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Stage 5: Suggested Actions */}
      {phaseReached('action') && (
        <div className="card-elevated p-3 fade-in-up">
          <div className="flex items-center gap-1.5 mb-2">
            <Icon name="ClipboardDocumentCheckIcon" size={13} className="text-primary" />
            <span className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground">Suggested Actions</span>
          </div>
          <div className="space-y-1.5">
            {[
              'Verify citizen identity',
              'Confirm registered mobile number',
              'Provide approved MySejahtera guidance',
              'Create service case if additional action is required',
            ].map((action, i) => (
              <div key={i} className="flex items-start gap-2 text-[12px]">
                <div className="w-4 h-4 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[9px] font-700 text-primary">{i + 1}</span>
                </div>
                <span className="text-foreground">{action}</span>
              </div>
            ))}
          </div>
          <div className="mt-2 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 font-600">
            Human review required — AI does not make autonomous Government decisions
          </div>
        </div>
      )}

      {/* Suggested Response */}
      {phaseReached('action') && callState === 'active' && (
        <div className="card-elevated p-3 fade-in-up">
          <div className="flex items-center gap-1.5 mb-2">
            <Icon name="ChatBubbleBottomCenterTextIcon" size={13} className="text-primary" />
            <span className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground">Suggested Response</span>
          </div>
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-2.5 text-[12px] text-foreground leading-relaxed mb-2.5 italic">
            "Encik Ahmed, untuk pertukaran nombor telefon bagi akaun MySejahtera, beberapa langkah pengesahan diperlukan. Saya boleh terangkan proses tersebut kepada encik."
          </div>
          <div className="flex gap-1.5">
            <button onClick={() => toast.success('Response noted — agent in control')} className="btn-primary px-3 py-1.5 text-[11px]">Use</button>
            <button onClick={() => toast.info('Response opened for editing')} className="btn-secondary px-3 py-1.5 text-[11px]">Edit</button>
          </div>
          <div className="text-[10px] text-muted-foreground mt-2">AI-suggested · Agent approval required · Not sent automatically</div>
        </div>
      )}

      {/* Cross-channel context */}
      <div className="card-elevated p-3">
        <div className="flex items-center gap-1.5 mb-1.5">
          <Icon name="ClockIcon" size={13} className="text-primary" />
          <span className="text-[10px] font-700 uppercase tracking-wide text-muted-foreground">Previous Interaction</span>
        </div>
        <div className="space-y-1.5 mb-2">
          <div className="text-[11px] text-foreground bg-blue-50 border border-blue-100 rounded-lg px-2 py-1.5">
            <div className="font-700 text-blue-700 mb-0.5">WhatsApp · Yesterday</div>
            <div>Passport Renewal Documentation</div>
            <div className="text-muted-foreground">Jabatan Imigresen Malaysia (JIM) · Resolved</div>
          </div>
          <div className="text-[10px] text-muted-foreground italic px-1">
            CX AI Assist automatically retrieved relevant context from the citizen's previous interaction.
          </div>
        </div>
        <button onClick={() => toast.info('Previous WhatsApp context: Passport Renewal Documentation — JIM — Resolved — Ticket: MYGCC-TKT-2026-010211')} className="text-[11px] text-primary font-600 hover:underline">
          View Context →
        </button>
      </div>

      <div className="text-[10px] text-muted-foreground text-center py-1 border-t border-border pt-2">
        CX AI Assist · Approved Government knowledge sources · Human review required
      </div>
    </div>
  );
}